import React, { useEffect, useRef, useState } from 'react';

import { PersonOrg, PersonOrgSubtype, PersonOrgType } from '@ndvr/customer-api';

import { CopyTaxIdButton } from '@/components/CopyTaxIdButton';
import { FetcherError, formatEin, formatTaxId } from 'utils';
import { usePersonOrgTaxId, useSetPersonOrgTaxId } from 'views/person/person-queries';
import { Button, HorizontalLayout, Text } from 'widgets';

const isForbiddenError = (error: unknown) => error instanceof FetcherError && error.httpStatus === 403;

const usesEinFormat = (personOrg: PersonOrg) =>
    personOrg.personOrgType === PersonOrgType.ORGANIZATION ||
    personOrg.personOrgSubtype === PersonOrgSubtype.IRREVOCABLE_TRUST;

/**
 * `onSaved` lets a view whose own query holds `identifyingTaxIdProvided` refresh it; `required` makes Set stand out
 * while the tax ID is missing.
 */
export const TaxIdCell = ({
    personOrg,
    onSaved,
    required,
}: {
    personOrg: PersonOrg;
    onSaved?: () => void;
    required?: boolean;
}) => {
    const isEin = usesEinFormat(personOrg);
    const { data, refetch, isFetching } = usePersonOrgTaxId(personOrg.personOrgGUID);
    const { mutate: setTaxId, isPending } = useSetPersonOrgTaxId(personOrg.personOrgGUID);
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [savedValue, setSavedValue] = useState<string | undefined>(undefined);
    const [forbidden, setForbidden] = useState(false);
    const [validationError, setValidationError] = useState<string | undefined>(undefined);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setIsEditing(false);
        setInputValue('');
        setSavedValue(undefined);
        setForbidden(false);
        setValidationError(undefined);
    }, [personOrg.personOrgGUID]);

    const isActuallyProvided = personOrg.identifyingTaxIdProvided || savedValue !== undefined;
    const displayValue = savedValue ?? data?.plainText;

    const handleReveal = async () => {
        const result = await refetch();
        if (isForbiddenError(result.error)) {
            setForbidden(true);
        }
    };

    const handleEdit = async () => {
        let value = displayValue;
        if (!value && isActuallyProvided) {
            const result = await refetch();
            if (isForbiddenError(result.error)) {
                setForbidden(true);
                return;
            }
            value = result.data?.plainText;
        }
        setInputValue(value ?? '');
        setIsEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSave = () => {
        const stripped = inputValue.replace(/-/g, '');
        if (!/^\d{9}$/.test(stripped)) {
            setValidationError(isEin ? 'EIN must be 9 digits' : 'Tax ID must be 9 digits');
            return;
        }
        setValidationError(undefined);
        setTaxId(stripped, {
            onSuccess: () => {
                setSavedValue(stripped);
                setIsEditing(false);
                onSaved?.();
            },
        });
    };

    const handleCancel = () => {
        setIsEditing(false);
        setInputValue('');
        setValidationError(undefined);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleSave();
        if (e.key === 'Escape') handleCancel();
    };

    if (forbidden) {
        return <Text className="font-medium text-red-500">No access</Text>;
    }

    const stopRowClick = {
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
        onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    };

    if (isEditing) {
        return (
            <div {...stopRowClick}>
                <HorizontalLayout $gap={4} $rowCenter>
                    <div className="flex flex-col gap-1">
                        <input
                            ref={inputRef}
                            className={`w-28 rounded border px-2 py-1 text-sm ${
                                validationError ? 'border-red-500' : 'border-gray-300'
                            }`}
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);
                                setValidationError(undefined);
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder={isEin ? 'XX-XXXXXXX' : 'XXX-XX-XXXX'}
                            disabled={isPending}
                        />
                        {validationError && <span className="text-xs text-red-500">{validationError}</span>}
                    </div>
                    <Button onClick={handleSave} buttonType="primary" size="small" waiting={isPending}>
                        Save
                    </Button>
                    <Button onClick={handleCancel} buttonType="secondary" size="small" disabled={isPending}>
                        Cancel
                    </Button>
                </HorizontalLayout>
            </div>
        );
    }

    if (!isActuallyProvided) {
        return (
            <div {...stopRowClick}>
                <HorizontalLayout $gap={8} $rowCenter>
                    <Text>Not provided</Text>
                    <Button onClick={handleEdit} buttonType={required ? 'primary' : 'secondary'} size="small">
                        Set
                    </Button>
                </HorizontalLayout>
            </div>
        );
    }

    return (
        <div {...stopRowClick}>
            <HorizontalLayout $gap={8} $rowCenter>
                {displayValue ? (
                    <>
                        <Text>{isEin ? formatEin(displayValue) : formatTaxId(displayValue)}</Text>
                        <CopyTaxIdButton value={displayValue} />
                    </>
                ) : (
                    <Button onClick={handleReveal} buttonType="secondary" size="small" waiting={isFetching}>
                        Reveal
                    </Button>
                )}
                <Button onClick={handleEdit} buttonType="secondary" size="small" waiting={isFetching}>
                    Edit
                </Button>
            </HorizontalLayout>
        </div>
    );
};
